import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/app/Logo";
import { CompanyCard } from "@/components/platform/CompanyCard";
import { NewCompanyDialog } from "@/components/platform/NewCompanyDialog";
import { LeadsList } from "@/components/platform/LeadsList";
import { PlatformSettingsCard } from "@/components/platform/PlatformSettingsCard";
import { ResetPasswordDialog } from "@/components/platform/ResetPasswordDialog";
import { CompanyEditDialog } from "@/components/platform/CompanyEditDialog";
import { GrantDialog } from "@/components/platform/GrantDialog";
import { ThemePicker } from "@/components/app/ThemePicker";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border border-slate-700/60 bg-slate-800/60 p-4">
    <div className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</div>
    <div className="mt-1 font-display text-3xl font-bold" data-testid={testid}>{value}</div>
  </div>
);

export default function Platform() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [granting, setGranting] = useState(null);

  const load = useCallback(() => api.get("/platform/companies").then((r) => setCompanies(r.data)).catch((e) => toast.error(errorMessage(e))), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    try {
      await api.patch(`/platform/companies/${c.id}`, { active: !c.active });
      toast.success(c.active ? `${c.company_name} bloqueada.` : `${c.company_name} ativada.`);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const run = async (c, path, msg) => {
    try {
      await api.post(`/platform/companies/${c.id}/${path}`);
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const archive = (c) => window.confirm(`Arquivar ${c.company_name}? Ninguém da empresa consegue entrar e a assinatura é cancelada. Os dados ficam guardados e você pode reativar depois.`)
    && run(c, "archive", `${c.company_name} arquivada.`);
  const restore = (c) => run(c, "restore", `${c.company_name} reativada.`);

  const current = companies.filter((c) => !c.archived);
  const archived = companies.filter((c) => c.archived);
  const active = current.filter((c) => c.active).length;
  const card = (c) => <CompanyCard key={c.id} c={c} isOwn={c.owner_email === user?.email} onToggle={toggle} onReset={setResetting}
    onEdit={setEditing} onArchive={archive} onRestore={restore} onGrant={setGranting} />;

  return (
    <div className="app-grain min-h-screen" data-testid="platform-page">
      <header className="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3 sm:px-6">
          <LogoMark className="h-9 w-9 shrink-0" />
          <div className="min-w-0 leading-tight">
            <div className="font-display text-lg font-extrabold tracking-tight">Painel do dono</div>
            <div className="text-xs text-slate-400">Ciclo Certo · empresas clientes</div>
          </div>
          <ThemePicker className="ml-auto" />
          <Button asChild variant="ghost" className="rounded-full" data-testid="platform-back-button">
            <Link to="/"><ArrowLeft className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Voltar ao app</span></Link>
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-5xl space-y-6 px-4 pb-16 pt-6 sm:px-6">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Empresas" value={current.length} testid="platform-total" />
          <Stat label="Ativas" value={active} testid="platform-active" />
          <Stat label="Bloqueadas" value={current.length - active} testid="platform-blocked" />
        </div>
        <div className="flex items-center justify-between">
          <h1 className="font-display text-lg font-bold">Empresas</h1>
          <Button onClick={() => setCreating(true)} className="rounded-full font-semibold" data-testid="platform-new-company-button"><Plus className="mr-1 h-4 w-4" /> Nova empresa</Button>
        </div>
        <div className="grid gap-3 md:grid-cols-2" data-testid="platform-company-list">
          {current.map(card)}
        </div>
        {archived.length > 0 && (
          <>
            <h2 className="font-display text-lg font-bold text-slate-400">Arquivadas ({archived.length})</h2>
            <div className="grid gap-3 md:grid-cols-2" data-testid="platform-archived-list">{archived.map(card)}</div>
          </>
        )}
        <h2 className="font-display text-lg font-bold">Assinatura e página de vendas</h2>
        <PlatformSettingsCard />
        <LeadsList />
      </main>
      <NewCompanyDialog open={creating} onClose={() => setCreating(false)} onCreated={load} />
      <ResetPasswordDialog company={resetting} onClose={() => setResetting(null)} />
      <CompanyEditDialog company={editing} onClose={() => setEditing(null)} onSaved={load} />
      <GrantDialog company={granting} onClose={() => setGranting(null)} onDone={load} />
    </div>
  );
}
